import { A, useLocation } from '@solidjs/router';
import { createEffect, splitProps, useContext, createContext, createSignal } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { cn, tv } from 'tailwind-variants';
import type { JSX, ComponentProps, Accessor, Setter, ParentProps } from 'solid-js';
import type { VariantProps } from 'tailwind-variants';

type Props = ComponentProps<typeof A> & JSX.HTMLAttributes<HTMLAnchorElement>;

export function Link(props: Props) {
	const location = useLocation();
	return (
		<Dynamic
			tabIndex={0}
			component={/^(\/|http)/.test(props.href) ? A : 'a'}
			aria-current={location.pathname === props.href ? 'page' : undefined}
			{...props}
			href={props.href}
			class={cn(
				'rounded-sm transition-all outline-none focus-visible:ring-2 focus-visible:ring-yellow-500/50 focus-visible:ring-offset-3 focus-visible:ring-offset-zinc-50 dark:focus-visible:ring-offset-zinc-950',
				props.class,
			)}
		/>
	);
}

type CTX = 'trail' | 'gravel' | 'road';
const Context = createContext<[Accessor<CTX>, Setter<CTX>]>();
export function LinkContext(props: ParentProps<{ variant: CTX }>) {
	// oxlint-disable-next-line solid/reactivity
	const [getter, setter] = createSignal<CTX>(props.variant);
	createEffect(() => {
		setter(props.variant);
	});
	return <Context.Provider value={[getter, setter]}>{props.children}</Context.Provider>;
}

export function useLinkContext() {
	const ctx = useContext(Context);
	return ctx?.[0] ?? (() => 'trail');
}

type TextLinkProps = VariantProps<typeof linkClassName>;
export function TextLink(props: Props & TextLinkProps) {
	const [, rest] = splitProps(props, ['color']);
	const ctx = useLinkContext();
	return (
		<Link
			tabIndex={0}
			{...rest}
			class={cn(linkClassName({ variant: props.variant ?? ctx(), color: props.color }), props.class)}
		/>
	);
}

export const linkClassName = tv({
	base: 'rounded-sm underline decoration-transparent decoration-0 -underline-offset-4 transition-all outline-none hover:decoration-current hover:decoration-1 hover:underline-offset-2 focus-visible:ring-2 focus-visible:ring-yellow-500/50 focus-visible:ring-offset-3 focus-visible:ring-offset-zinc-50 dark:focus-visible:ring-offset-zinc-950',
	variants: {
		variant: {
			gravel: 'hover:decoration-dotted',
			trail: 'hover:decoration-wavy',
			road: 'hover:decoration-solid',
		},
		color: {
			current: 'text-current no-underline hover:text-yellow-600 hover:underline dark:text-current',
			default: 'text-yellow-600 dark:text-yellow-400',
		},
	},
	defaultVariants: {
		variant: 'trail',
		color: 'default',
	},
});
